import { Text } from '@user-portal/wikr-components';
import { trackBackButtonClick, trackExercisePageLoad } from 'analytics/trackers/exercise';
import WorkoutsContainer from 'containers/Workouts';
import React, { ComponentType } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { PRIVATE } from 'router/routes';

import { useScreenLoadTracker, useSelector } from 'hooks';
import { useWorkout } from 'hooks/workout/useWorkout';

import CompleteWorkoutModal from 'components/Modals/CompleteWorkoutModal';
import { WorkoutTitle } from 'components/WorkoutTitle';

import { ExerciseDetails, ExercisePlayer, WorkoutNavigationBar } from './components';
import SidebarExercises from './components/SidebarExercises';
import WorkoutProgressInfo from './components/WorkoutProgressInfo';
import { useWorkoutContext } from './hooks';
import WorkoutProvider from './WorkoutProvider';

import * as S from './styled';

const ProtectPage = (Component: ComponentType) => () => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { workout } = useSelector((state) => state.workouts);

    if (!workout) {
        return <Navigate to={PRIVATE.WORKOUT_BUILDER.path} />;
    }

    return (
        <WorkoutProvider>
            <Component />
        </WorkoutProvider>
    );
};

export const WorkoutPage = ProtectPage(() => {
    useScreenLoadTracker(trackExercisePageLoad);

    const navigate = useNavigate();
    const { state } = useLocation();
    const { workoutExercises, name } = useWorkout();

    const returnTo = state?.returnTo ?? PRIVATE.WORKOUT_BUILDER.path;

    const {
        fullWorkoutTime,
        handleExerciseClick,
        handleProgress,
        exerciseData,
        currentPlaylistPosition,
        playerContainer,
        completeData,
        currentExercise,
        isPageBlocked,
    } = useWorkoutContext();

    return (
        <>
            <WorkoutsContainer
                withBackButton
                trackOnBack={() => trackBackButtonClick(exerciseData.id, currentPlaylistPosition)}
                testId="workout-player-page"
            >
                <S.WorkoutPlayerContainer ref={playerContainer}>
                    <S.WorkoutTitleWrapper>
                        <WorkoutTitle name={name} title="workout.custom.title" subtitle="workout.custom.subtitle" />
                    </S.WorkoutTitleWrapper>

                    <S.PlayerContainer>
                        <WorkoutProgressInfo withClose />

                        <ExercisePlayer
                            playlistPosition={currentPlaylistPosition}
                            exerciseTime={currentExercise.time}
                            onProgress={handleProgress}
                            isStop={Boolean(completeData || isPageBlocked)}
                        />
                        <S.InfoBlock>
                            <Text type="medium-text" medium mb={12}>
                                {exerciseData?.name}
                            </Text>

                            <WorkoutNavigationBar />
                        </S.InfoBlock>
                    </S.PlayerContainer>

                    <ExerciseDetails exerciseId={currentExercise.id} playlistPosition={currentPlaylistPosition} />

                    <S.Sidebar>
                        <SidebarExercises
                            handleExerciseClick={handleExerciseClick}
                            currentExerciseId={currentExercise.id}
                        />
                    </S.Sidebar>
                </S.WorkoutPlayerContainer>
            </WorkoutsContainer>

            <CompleteWorkoutModal
                workoutData={{
                    duration: fullWorkoutTime,
                    exercises: workoutExercises.length,
                    burned: completeData?.calories_burned,
                }}
                isOpen={Boolean(completeData)}
                onClose={() => navigate(returnTo)}
            />
        </>
    );
});
